// Catálogo de Desafios diários do painel admin.
//
// Um calendário do mês (dias com desafio marcados) e, abaixo, a tabela do mesmo mês.
// Desafio futuro (`editable`) pode ser editado/excluído; de hoje ou passado só é visto.
// Datas da API passam sempre por `challengeDay()` — nunca `moment(iso)` (UTC−3).
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Calendar,
  Col,
  DatePicker,
  Form,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  message,
} from 'antd';
import moment from 'moment';
import {
  createDailyChallenge,
  deleteDailyChallenge,
  listDailyChallenges,
  updateDailyChallenge,
  type DailyChallenge,
  type DailyChallengeItem,
  type PickableGame,
} from '../../services/admin';
import {
  apiErrorMessage,
  applyPickerChange,
  challengeDay,
  formatDay,
  fromChallengeItems,
  gameLine,
  isDisabledDay,
  monthRange,
  toItemInputs,
} from './desafios';
import { DesafioGameOrder } from './DesafioGameOrder';
import { TorneioGamePicker } from './TorneioGamePicker';

interface Props {
  /** Costura de teste: o "hoje" da tela. */
  today?: moment.Moment;
}

const DAY_KEY = 'YYYY-MM-DD';

export const DesafiosCatalog: React.FC<Props> = (props) => {
  const [today] = useState(() => (props.today ?? moment()).clone().startOf('day'));
  const [month, setMonth] = useState(() => today.clone().startOf('month'));
  const [challenges, setChallenges] = useState<DailyChallenge[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState<DailyChallenge | null>(null);
  const [editing, setEditing] = useState<DailyChallenge | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<PickableGame[]>([]);
  const [form] = Form.useForm<{ date: moment.Moment }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setChallenges(await listDailyChallenges(monthRange(month)));
    } catch {
      message.error('Falha ao carregar os desafios.');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const byDay = useMemo(
    () => new Map(challenges.map((c) => [challengeDay(c.date), c] as const)),
    [challenges],
  );

  const sorted = useMemo(
    () => [...challenges].sort((a, b) => challengeDay(a.date).localeCompare(challengeDay(b.date))),
    [challenges],
  );

  const openView = (c: DailyChallenge): void => setViewing(c);

  const openCreate = (day?: moment.Moment): void => {
    setEditing(null);
    setSelected([]);
    form.resetFields();
    if (day) form.setFieldsValue({ date: day });
    setFormOpen(true);
  };

  const openEdit = (c: DailyChallenge): void => {
    setEditing(c);
    // Cópias do desafio: no PUT voltam com o próprio id e são mantidas.
    setSelected(fromChallengeItems(c.items));
    form.resetFields();
    form.setFieldsValue({ date: moment(challengeDay(c.date), DAY_KEY) });
    setFormOpen(true);
  };

  const closeForm = (): void => setFormOpen(false);

  const handlePickerChange = (next: PickableGame[]): void => {
    const { games, overflow } = applyPickerChange(selected, next);
    if (overflow) {
      message.warning('Máximo de 5 jogos por desafio.');
      return;
    }
    setSelected(games);
  };

  const handleSubmit = async (): Promise<void> => {
    let v: { date: moment.Moment };
    try {
      v = await form.validateFields();
    } catch {
      return;
    }
    if (selected.length === 0) {
      message.error('Selecione ao menos um jogo.');
      return;
    }
    const payload = { date: v.date.format(DAY_KEY), items: toItemInputs(selected) };
    setSaving(true);
    try {
      if (editing) {
        await updateDailyChallenge(editing.id, payload);
        message.success('Desafio atualizado.');
      } else {
        await createDailyChallenge(payload);
        message.success('Desafio criado.');
      }
      closeForm();
      await load();
    } catch (err: unknown) {
      // Modal continua aberto com a seleção intacta (ex.: 409 de data ocupada).
      message.error(apiErrorMessage(err) ?? 'Falha ao salvar o desafio.');
    } finally {
      setSaving(false);
    }
  };

  const openDay = (day: moment.Moment): void => {
    const c = byDay.get(day.format(DAY_KEY));
    if (c) {
      if (c.editable) openEdit(c);
      else openView(c);
    } else if (!day.isBefore(today, 'day')) {
      openCreate(day);
    }
  };

  const handleDelete = async (c: DailyChallenge): Promise<void> => {
    try {
      await deleteDailyChallenge(c.id);
      message.success('Desafio excluído.');
      await load();
    } catch (err: unknown) {
      message.error(apiErrorMessage(err) ?? 'Falha ao excluir o desafio.');
    }
  };

  const columns = [
    {
      title: 'Data',
      dataIndex: 'date',
      width: 120,
      render: (date: string) => formatDay(challengeDay(date)),
    },
    {
      title: 'Jogos',
      dataIndex: 'items',
      render: (items: DailyChallengeItem[]) =>
        [...items]
          .sort((a, b) => a.position - b.position)
          .map((item) => <div key={item.position}>{gameLine(item.position, item)}</div>),
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 180,
      render: (_: unknown, c: DailyChallenge) =>
        c.editable ? (
          <Space>
            <Button size="small" onClick={() => openEdit(c)}>
              Editar
            </Button>
            <Popconfirm
              title="Excluir este desafio? Os jogos copiados também serão apagados."
              okText="Sim, excluir"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar"
              onConfirm={() => handleDelete(c)}
            >
              <Button size="small" danger>
                Excluir
              </Button>
            </Popconfirm>
          </Space>
        ) : (
          <Button size="small" onClick={() => openView(c)}>
            Ver
          </Button>
        ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Desafios</h2>
        <Button type="primary" onClick={() => openCreate()}>
          Novo desafio
        </Button>
      </div>

      <Calendar
        defaultValue={today}
        dateCellRender={(d) => {
          const c = byDay.get(d.format(DAY_KEY));
          return c ? <Badge status="success" text={`${c.items.length} jogo(s)`} /> : null;
        }}
        onPanelChange={(d) => setMonth(d.clone().startOf('month'))}
        onSelect={(d) => {
          // No antd 4.21 o onSelect também dispara na troca de mês (cabeçalho ou dia do mês
          // vizinho); o onPanelChange já recarregou o mês.
          if (!d.isSame(month, 'month')) return;
          openDay(d);
        }}
      />

      <Table
        style={{ marginTop: 16 }}
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={sorted}
        pagination={false}
      />

      <Modal
        title={viewing ? `Desafio de ${formatDay(challengeDay(viewing.date))}` : undefined}
        visible={viewing !== null}
        onCancel={() => setViewing(null)}
        footer={<Button onClick={() => setViewing(null)}>Fechar</Button>}
        destroyOnClose
      >
        {viewing && <DesafioGameOrder value={fromChallengeItems(viewing.items)} readOnly />}
      </Modal>

      <Modal
        title={
          editing ? `Editar desafio de ${formatDay(challengeDay(editing.date))}` : 'Novo desafio'
        }
        visible={formOpen}
        onOk={handleSubmit}
        onCancel={closeForm}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
        width={1000}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="date" label="Data" rules={[{ required: true, message: 'Informe a data.' }]}>
            <DatePicker
              format="DD/MM/YYYY"
              disabledDate={(d) => isDisabledDay(d, today, editing ? 'edit' : 'create')}
            />
          </Form.Item>
        </Form>
        <Row gutter={16}>
          <Col span={14}>
            <TorneioGamePicker value={selected} onChange={handlePickerChange} />
          </Col>
          <Col span={10}>
            <DesafioGameOrder value={selected} onChange={setSelected} />
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default DesafiosCatalog;
